import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type {
  MultiRootEditor,
} from 'ckeditor5';
import {
  UserDTO,
} from 'dtos';
import {
  getDisplayName,
} from 'pages/Dashboard/utils/helper';
import getToDoAvatarClass from 'pages/Dashboard/pages/Todos/avatarColors';
import {
  useToDoMentionUsers,
} from 'pages/Dashboard/pages/Todos/hooks';

type MentionFeedItem = {
  id: string;
  userId: number;
  name: string;
  initials: string;
};

type MentionAttribute = {
  id?: unknown;
  userId?: unknown;
};

const emptyMentionUserIdLookup = new Map<string, number>();

function normalizeMentionId(mentionId: string) {
  return mentionId.trim().toLowerCase();
}

function createMentionUserIdLookup(mentionItems: MentionFeedItem[]) {
  return new Map(mentionItems.map(({ id, userId }) => [normalizeMentionId(id), userId]));
}

function resolveMentionUserId(
  mention: MentionAttribute | undefined,
  mentionUserIdLookup: ReadonlyMap<string, number>,
) {
  // CKEditor preserves the mention ID when copying, but omits custom feed metadata.
  return typeof mention?.userId === 'number'
    ? mention.userId
    : mentionUserIdLookup.get(
      typeof mention?.id === 'string' ? normalizeMentionId(mention.id) : '',
    );
}

function haveSameUserIds(currentUserIds: number[], nextUserIds: number[]) {
  return currentUserIds.length === nextUserIds.length
    && currentUserIds.every((userId, index) => userId === nextUserIds[index]);
}

function getMentionInitials(user: UserDTO, fallbackName: string) {
  return user?.formattedNames?.initials
    ?? fallbackName
      .split(' ')
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase() ?? '')
      .join('');
}

export function getMentionableToDoUsers(users: UserDTO[] = []) {
  return users.filter((user) => (
    user?.isActive
    && !user?.isServiceAccount
    && typeof user?.userId === 'number'
  ));
}

export function getUserIds(users: UserDTO[] = []) {
  return users.reduce<number[]>((userIds, user) => {
    if (typeof user?.userId === 'number') {
      userIds.push(user.userId);
    }

    return userIds;
  }, []);
}

export function mergeTaggedUserIds(...userIdCollections: Array<number[] | null | undefined>) {
  const userIds = new Set<number>();

  userIdCollections.forEach((collection) => {
    collection?.forEach((userId) => {
      if (Number.isInteger(userId) && userId > 0) {
        userIds.add(userId);
      }
    });
  });

  return Array.from(userIds);
}

function getMentionFeedItems(users: UserDTO[] = []): MentionFeedItem[] {
  return users.reduce<MentionFeedItem[]>((items, user) => {
    if (typeof user?.userId !== 'number') {
      return items;
    }

    const name = getDisplayName(user.formattedNames, '').trim();

    if (!name) {
      return items;
    }

    items.push({
      id: `@${name}`,
      userId: user.userId,
      name,
      initials: getMentionInitials(user, name),
    });

    return items;
  }, []);
}

function renderMentionItem(item: unknown) {
  const mentionItem = item as MentionFeedItem;
  const element = document.createElement('span');
  element.className = 'todo-mention-item flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left transition-colors';

  const avatar = document.createElement('span');
  avatar.className = `todo-mention-avatar ${getToDoAvatarClass(mentionItem.userId)} flex h-6 w-6 items-center justify-center rounded-full text-[10px] font-semibold`;
  avatar.textContent = mentionItem.initials;

  const label = document.createElement('span');
  label.className = 'todo-mention-label text-xs font-medium';
  label.textContent = mentionItem.name;

  element.append(avatar, label);

  return element;
}

export function getTaggedUserIdsFromEditor(
  editor: MultiRootEditor | null,
  mentionUserIdLookup: ReadonlyMap<string, number> = emptyMentionUserIdLookup,
): number[] {
  const root = editor?.model.document.getRoot('content');

  if (!root || !editor) {
    return [];
  }

  const taggedUserIds = new Set<number>();

  for (const item of editor.model.createRangeIn(root).getItems()) {
    if (item.hasAttribute('mention')) {
      const mention = item.getAttribute('mention') as MentionAttribute | undefined;
      const userId = resolveMentionUserId(mention, mentionUserIdLookup);

      if (typeof userId === 'number') {
        taggedUserIds.add(userId);
      }
    }
  }

  return Array.from(taggedUserIds);
}

export function useToDoMentions() {
  const { data: users = [] } = useToDoMentionUsers();
  const mentionUsers = useMemo(
    () => getMentionableToDoUsers(users),
    [users],
  );
  const mentionItems = useMemo(
    () => getMentionFeedItems(mentionUsers)
      .sort((left, right) => left.name.localeCompare(right.name, undefined, { sensitivity: 'base' })),
    [mentionUsers],
  );
  const mentionUserIdLookup = useMemo(
    () => createMentionUserIdLookup(mentionItems),
    [mentionItems],
  );
  const mentionItemsRef = useRef<MentionFeedItem[]>(mentionItems);
  const editorRef = useRef<MultiRootEditor | null>(null);
  const [taggedUserIds, setTaggedUserIds] = useState<number[]>([]);

  useEffect(() => {
    mentionItemsRef.current = mentionItems;
  }, [mentionItems]);

  const syncTaggedUserIds = useCallback((editor: MultiRootEditor) => {
    const nextUserIds = getTaggedUserIdsFromEditor(editor, mentionUserIdLookup);

    setTaggedUserIds((currentUserIds) => (
      haveSameUserIds(currentUserIds, nextUserIds) ? currentUserIds : nextUserIds
    ));
  }, [mentionUserIdLookup]);

  useEffect(() => {
    if (editorRef.current) {
      syncTaggedUserIds(editorRef.current);
    }
  }, [syncTaggedUserIds]);

  const mentionsConfig = useMemo(() => ({
    mention: {
      dropdownLimit: Infinity,
      feeds: [{
        marker: '@',
        minimumCharacters: 0,
        feed: (queryText: string) => {
          const normalizedQuery = queryText.trim().toLowerCase();

          return mentionItemsRef.current.filter((item) => (
            normalizedQuery.length === 0
              || item.name.toLowerCase().includes(normalizedQuery)
          ));
        },
        itemRenderer: renderMentionItem,
      }],
    },
  }), []);
  const handleEditorChange = useCallback((_: string, editor: MultiRootEditor) => {
    editorRef.current = editor;
    syncTaggedUserIds(editor);
  }, [syncTaggedUserIds]);

  const resetTaggedUserIds = useCallback(() => {
    editorRef.current = null;
    setTaggedUserIds([]);
  }, []);

  const getMergedTaggedUserIds = useCallback((...userIdCollections: Array<number[] | null | undefined>) => (
    mergeTaggedUserIds(taggedUserIds, ...userIdCollections)
  ), [taggedUserIds]);

  return {
    mentionUsers,
    mentionsConfig,
    handleEditorChange,
    resetTaggedUserIds,
    getMergedTaggedUserIds,
    taggedUserIds,
  };
}
